import React from "react";
import tw from "twin.macro";
import PlatformDetailPage from "./PlatformDetailPage";
import PMDetailPage from "./PMDetailPage";
import SlackDetailPage from "./SlackDetailPage";
import GoogleDetailPage from "./GoogleDetailPage";

const Container = tw.div`px-4`;
const Inner = tw.div`max-w-6xl mx-auto`;
const Header = tw.div`text-center mb-12`;
const Title = tw.h2`text-3xl md:text-4xl font-bold text-secondary-500 mb-4`;
const Grid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6`;
const GroupSection = tw.div`mb-12`;
const Card = tw.div`rounded-2xl p-6 border border-gray-300 bg-white transition-all duration-300 hover:border-primary-500 hover:shadow-lg`;
const CompetitorName = tw.h3`text-xl font-bold text-secondary-500 mb-2`;
const Description = tw.p`text-gray-600 text-sm mb-4 leading-relaxed`;
const CategoryBadge = tw.span`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-4 bg-green-500 text-primary-500`;
const CompareLink = tw.a`inline-flex items-center text-sm font-semibold cursor-pointer transition-all duration-200 no-underline text-primary-500 hover:text-primary-700`;
const ArrowIcon = tw.svg`w-4 h-4 ml-1 transition-transform duration-200`;

export const features = [
  "Out of the box",
  "Jobs / Tasks / Grouped tasks",
  "Backlog handling",
  "Voting",
  "Status at a glance",
  "Wizard help with operations",
  "Move / convert any to any",
  "Smart notifications",
  "Async first maker focus",
  "CLI / Github actions",
];

export const privateWorkspaceCompetitors = [
  {
    name: "Google Docs / Sheets",
    slug: "document-tools",
    detailPage: <GoogleDetailPage />,
    description: "Missing backlog handling, wizards, and smart notifications.",
    category: "Document Tools",
    features: {
      "Out of the box": false,
      "Jobs / Tasks / Grouped tasks": true,
      "Backlog handling": "Only DIY.",
      "Voting": false,
      "Status at a glance": false,
      "Wizard help with operations": false,
      "Move / convert any to any": true,
      "Smart notifications": false,
      "Async first maker focus": true,
      "CLI / Github actions": false,
    },
  },
  {
    name: "Slack / Teams",
    slug: "chat-apps",
    detailPage: <SlackDetailPage />,
    description: "Chat first requires immediate responses which break async maker focus.",
    category: "Chat Apps",
    features: {
      "Out of the box": false,
      "Jobs / Tasks / Grouped tasks": "Not natively.",
      "Backlog handling": false,
      "Voting": true,
      "Status at a glance": false,
      "Wizard help with operations": false,
      "Move / convert any to any": false,
      "Smart notifications": true,
      "Async first maker focus": "Must answer immediately.",
      "CLI / Github actions": false,
    },
  },
];

export const noPrivateWorkspaceCompetitors = [
  {
    name: "Notion / Clickup / Monday",
    slug: "platforms",
    detailPage: <PlatformDetailPage />,
    description: "Spreadsheet style with awkward task hierarchies and no assistance section.",
    category: "Platforms",
    features: {
      "Out of the box": false,
      "Jobs / Tasks / Grouped tasks": "Awkwardly.",
      "Backlog handling": "Only DIY.",
      "Voting": true,
      "Status at a glance": "No assistance section.",
      "Wizard help with operations": false,
      "Move / convert any to any": false,
      "Smart notifications": false,
      "Async first maker focus": "Only DIY.",
      "CLI / Github actions": false,
    },
  },
  {
    name: "Jira / Asana / Linear / Github",
    slug: "project-management",
    detailPage: <PMDetailPage />,
    description: "Traditional PM tools without wizard help, built-in stages, or async-first design.",
    category: "Project Management",
    features: {
      "Out of the box": true,
      "Jobs / Tasks / Grouped tasks": "Very poor levels presentation.",
      "Backlog handling": "Only one type; no bugs and jobs.",
      "Voting": false,
      "Status at a glance": "No assistance section.",
      "Wizard help with operations": false,
      "Move / convert any to any": true,
      "Smart notifications": false,
      "Async first maker focus": "Meeting based.",
      "CLI / Github actions": true,
    },
  }
];

const CompetitorCard = ({ competitor }) => (
  <Card>
    <CompetitorName>{competitor.name}</CompetitorName>
    <Description>{competitor.description}</Description>
    <CategoryBadge>{competitor.category}</CategoryBadge>
    <div>
      <CompareLink href={`/versus/${competitor.slug}`}>
        Compare with Uclusion
        <ArrowIcon
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2.5"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </ArrowIcon>
      </CompareLink>
    </div>
  </Card>
);

const Comparison = () => {
  return (
    <Container id='comparison' style={{ marginTop: '4rem', marginBottom: '14rem' }}>
      <Inner>
        <Header>
          <Title>Comparison with tools built for DIY or project managers</Title>
        </Header>

        <GroupSection>
          <Grid>
            {privateWorkspaceCompetitors.map((competitor, index) => (
              <CompetitorCard key={index} competitor={competitor} />
            ))}
          </Grid>
        </GroupSection>

        <GroupSection>
          <Grid>
            {noPrivateWorkspaceCompetitors.map((competitor, index) => (
              <CompetitorCard key={index} competitor={competitor} />
            ))}
          </Grid>
        </GroupSection>
      </Inner>
    </Container>
  );
};

export default Comparison;
